import { useEffect, useRef, useState, useContext } from 'react';
import { getShortLanguageCode, useDictionary } from 'lib/utils';
import { LanguageInfo, LanguageSwitcherProps } from './HeaderTypes';
import Icon from 'components/helpers/Icon';
import classNames from 'classnames/bind';
import styles from './Header.module.scss';
import LanguageList from './LanguageList';
import { contextType, HeaderContext } from './Header';

const cx = classNames.bind(styles);

const loadShortLanguageCodes = (pageLanguages: LanguageInfo[]) => {
  if (!pageLanguages) {
    return;
  }

  for (const lang of pageLanguages) {
    if (!lang.shortCode) {
      lang.shortCode = getShortLanguageCode(lang.code);
    }
  }
};

const LanguageSwitcher = (props: LanguageSwitcherProps): React.JSX.Element | null => {
  const { currentLang, datasourceLanguage } = useContext(HeaderContext) as contextType;

  const { pageLanguages, currentLanguage, currentPage, isMobile } = props;
  const currentLanguageCode = getShortLanguageCode(currentLanguage);

  const [showMenu, setShowMenu] = useState(false);
  const [browserAndSitecoreLanguagesMatch, setBrowserAndSitecoreLanguagesMatch] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  pageLanguages && loadShortLanguageCodes(pageLanguages);

  const onCloseCallback = () => setShowMenu(false);

  useEffect(() => {
    const $browserLanguageCode = getShortLanguageCode(navigator.language);
    setBrowserAndSitecoreLanguagesMatch($browserLanguageCode === currentLanguageCode);

    const hideMenu = () => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideMenu();
      }
    };

    const handleOutsideClick = (event: Event) => {
      if (!ref.current?.contains(event.target as Element) && !isMobile) {
        hideMenu();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    window.addEventListener('mousedown', handleOutsideClick);

    return () => {
      window.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [ref, currentLanguageCode, showMenu, isMobile, setBrowserAndSitecoreLanguagesMatch]);

  const $ariaLabelChangeLanguage = useDictionary('Change Language');

  // If there is just 1 language, do not render the language switcher
  if (!currentLanguage || !pageLanguages || pageLanguages?.length < 2) {
    return null;
  }

  return (
    <>
      <div className={cx('language-switcher', 'flyoutContainer')} ref={ref} tabIndex={-1}>
        <button
          type="button"
          aria-controls="language-items"
          aria-expanded={showMenu}
          aria-label={$ariaLabelChangeLanguage}
          title={$ariaLabelChangeLanguage}
          className={cx({ selected: showMenu, highlighted: !browserAndSitecoreLanguagesMatch })}
          onClick={() => setShowMenu(!showMenu)}
          lang={datasourceLanguage ?? currentLang}
        >
          <span>{currentLanguageCode}</span>
          <Icon variant="globe" className={cx('globe')} />
        </button>

        {!isMobile && showMenu ? (
          <LanguageList
            currentLanguage={currentLanguage}
            currentPage={currentPage}
            pageLanguages={pageLanguages}
            isMobile={isMobile}
            displayMenu={showMenu}
          />
        ) : null}
      </div>

      {/* Mobile display is fixed at bottom of the screen - its easier to manage outside parent div*/}
      {isMobile && showMenu ? (
        <LanguageList
          currentLanguage={currentLanguage}
          currentPage={currentPage}
          pageLanguages={pageLanguages}
          isMobile={isMobile}
          displayMenu={showMenu}
          onClose={onCloseCallback}
          displayType="mobile"
        />
      ) : null}
    </>
  );
};

export default LanguageSwitcher;
